import React, { useState, useEffect } from "react";

import { Button, Icon } from "rbx";

import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import gql from "graphql-tag";

import { useApolloClient, useQuery } from "react-apollo";
import { OrdersTable } from "../../../../components/Orders";
import { UPDATE_ORDER_MUTATION } from "../../../../graphql/Orders/Orders.graphql";
import Loading from "../../../../components/Loading";
import { useModal } from "../../../../components/ModalProvider";
import Confirmation from "../../../../components/modals/Confirmation";
import {
  ourToast as toast,
  runPromisesInSeries,
  uniqueElementsBy
} from "../../../../utils";
import { BILLING_VERIFIED_INVOICE_COLUMNS } from "../columns";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const ALL_ORDERS_INV_GEN_QUERY = gql`
  query ALL_ORDERS_QUERY($where: OrderWhereInput!) {
    Orders(where: $where) {
      RecId
      InvoiceNumber
      InvoiceBatchNumber
      InvoiceDate
    }
  }
`;

const ExportedToAccounting = () => {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["INV", "EXA", "BP", "IPA"]
      }
    }
  });

  const client = useApolloClient();
  const history = useHistory();
  const { setModalOpen } = useModal();
  const [loading, setLoading] = useState(false);
  const [States, setStates] = useState(null);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);

  const [where, setWhere] = useState(null);

  useEffect(() => {
    if (
      SystemCodes?.data?.SystemCodes?.length > 0 &&
      !States &&
      !where?.StatusRecIdRef
    ) {
      const ExportedToAccountingState = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "EXA"
      );
      setStates({
        ImportedInAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "IPA"
        ),
        InvoiceGenerated: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "INV"
        ),
        ExportedToAccounting: ExportedToAccountingState,
        BilledAndPosted: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "BP"
        )
      });

      setWhere({ StatusRecIdRef: ExportedToAccountingState.RecId });
    }
  }, [States, SystemCodes, SystemCodes.data, where]);

  const handleResetToInvoiceGenerated = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be reset");
      }
      setLoading(true);

      await runPromisesInSeries(
        selectedData?.map((RecId, index) => {
          const obj = {
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              data: {
                StatusRecIdRef: States.InvoiceGenerated.RecId,
                InvoiceBatchNumber: null,
                InvoiceNumber: null,
                InvoiceDateTime: null
              },
              where: {
                RecId
              }
            }
          };
          if (index === selectedData.length - 1) {
            obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
          }
          return () => client.mutate(obj);
        })
      );
      setLoading(false);
      toast.success("Orders reset successfully");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const printInvoices = selectedData => {
    if (selectedData.length > 0) {
      const dataToPrint = selectedData.filter(d => d || null);
      const {
        REACT_APP_REPORT_BASE_URL = "https://gdev.gravoc.com"
      } = process.env;
      const BASE_URL = `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Invoice`;
      const REPORT_URL = `${BASE_URL}&Param=InvoiceNumber&ParamValue=${dataToPrint.join()}`;
      window.open(REPORT_URL);
      setSelectedFlatRows([]);
    } else {
      toast.error("Please select some invoice");
    }
  };

  const handleImportToAccounting = async () => {
    try {
      const selectedData = selectedFlatRows.map(x => x.InvoiceBatchNumber);

      if (!selectedData.length) {
        throw Error("Please select some orders to be exported");
      }
      const uniqueElements = uniqueElementsBy(selectedData, (a, b) => a === b);
      setLoading(true);
      const importDataToAccounting = async () => {
        const { data: ordersData } = await client.query({
          query: ALL_ORDERS_INV_GEN_QUERY,
          variables: {
            where: {
              InvoiceBatchNumber_in: uniqueElements,
              StatusRecIdRef: States.ExportedToAccounting.RecId
            }
          }
        });
        await runPromisesInSeries(
          ordersData.Orders.map(({ RecId }, index) => {
            const obj = {
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: {
                  StatusRecIdRef: States.ImportedInAccounting.RecId
                },
                where: {
                  RecId
                }
              }
            };
            if (index === ordersData.Orders.length - 1) {
              obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
            }
            return () => client.mutate(obj);
          })
        );

        toast.success("Orders exported successfully");
      };
      const message = `Do you really want to update to Import to Accounting all invoices in Batch Number(s) ${uniqueElements.join(
        ", "
      )}?`;

      setModalOpen(
        true,
        <Confirmation
          message={message}
          onCancel={() => {
            setModalOpen(false);
          }}
          onConfirm={async () => {
            await importDataToAccounting();
            setModalOpen(false);
          }}
        />
      );
    } catch (err) {
      toast.error("Error exporting Orders.");
    } finally {
      setLoading(false);
    }
  };
  if (loading || SystemCodes?.loading || !States) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <OrdersTable
        callToAction1={handleResetToInvoiceGenerated}
        callToAction2={handleImportToAccounting}
        columns={BILLING_VERIFIED_INVOICE_COLUMNS(true)}
        initialSortBy={[
          {
            id: "InvoiceNumber",
            desc: false
          },
          { id: "OrderNumber", desc: false }
        ]}
        pageHeadStart={
          <Button
            color="primary"
            disabled={!selectedFlatRows.length}
            size="small"
            onClick={handleImportToAccounting}
          >
            <Icon>
              <FontAwesomeIcon icon="file-export" />
            </Icon>
            <span>Import To Accounting</span>
          </Button>
        }
        printInvoices={printInvoices}
        selectedFlatRows={selectedFlatRows}
        setSelectedFlatRows={setSelectedFlatRows}
        title=""
        where={where ?? {}}
        onCellClick={cell => {
          if (cell.column.id !== "selection") {
            history.push(`/dispatch/orders/${cell.row.original.RecId}/BOL-ELD`);
          }
        }}
      />
    </React.Fragment>
  );
};
ExportedToAccounting.propTypes = {};
export default ExportedToAccounting;
